import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, ExternalLink, Check, X, Clock, AlertTriangle, Building2, RefreshCcw,
  Megaphone, XCircle, Send,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All' },
];

const DEFAULT_LEAD_FEE = 100;
const DEFAULT_REV_SHARE = 10;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function relativeDate(iso) {
  if (!iso) return '—';
  try {
    const dt = new Date(iso);
    const diff = Math.floor((Date.now() - dt.getTime()) / 1000);
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return dt.toLocaleDateString();
  } catch {
    return '—';
  }
}

function StatusPill({ status }) {
  const map = {
    pending: { cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30', label: 'Pending', Icon: Clock },
    approved: { cls: 'bg-green-500/10 text-green-400 border-green-500/30', label: 'Approved', Icon: Check },
    rejected: { cls: 'bg-red-500/10 text-red-400 border-red-500/30', label: 'Rejected', Icon: X },
  };
  const entry = map[status] || map.pending;
  const { cls, label, Icon } = entry;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${cls}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function TermsDelta({ app }) {
  const leadDelta = app.proposed_per_lead_fee_usd - DEFAULT_LEAD_FEE;
  const shareDelta = app.proposed_rev_share_pct - DEFAULT_REV_SHARE;
  return (
    <div className="space-y-1 text-xs">
      <div className="flex items-center gap-2">
        <span className="text-neutral-400">Per lead:</span>
        <span className="text-white font-semibold">${app.proposed_per_lead_fee_usd}</span>
        {leadDelta !== 0 ? (
          <span className={leadDelta > 0 ? 'text-green-400' : 'text-yellow-400'}>
            ({leadDelta > 0 ? '+' : ''}{leadDelta} vs default)
          </span>
        ) : (
          <span className="text-neutral-500">(default)</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-neutral-400">Rev share:</span>
        <span className="text-white font-semibold">{app.proposed_rev_share_pct}%</span>
        {Math.abs(shareDelta) > 0.01 ? (
          <span className={shareDelta > 0 ? 'text-green-400' : 'text-yellow-400'}>
            ({shareDelta > 0 ? '+' : ''}{shareDelta.toFixed(1)}pp vs default)
          </span>
        ) : (
          <span className="text-neutral-500">(default)</span>
        )}
      </div>
    </div>
  );
}

function ApplicationDrawer({ application, onClose, onAction }) {
  const [busy, setBusy] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectBox, setShowRejectBox] = useState(false);

  if (!application) return null;

  const act = async (kind) => {
    setBusy(kind);
    try {
      const url = `${API_URL}/api/admin/build-partner-applications/${application.application_id}/${kind}`;
      const body = kind === 'reject' ? { note: rejectNote } : {};
      await axios.post(url, body, { headers: authHeaders() });
      toast.success(kind === 'approve' ? 'Approved. Partner is now live on the marketplace.' : 'Application rejected.');
      onAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${kind} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="build-partner-drawer"
    >
      <div
        className="bg-neutral-950 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-white/10 flex items-start justify-between gap-4 sticky top-0 bg-neutral-950 z-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <StatusPill status={application.status} />
              <span className="text-xs text-neutral-500">
                Submitted {relativeDate(application.created_at)}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-white truncate">{application.company_name}</h2>
            <a
              href={application.website_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-[#00F0FF] hover:underline inline-flex items-center gap-1"
            >
              {application.website_url}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="build-partner-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Contact</div>
            <div className="text-white">{application.contact_name}{application.contact_role ? ` · ${application.contact_role}` : ''}</div>
            <a href={`mailto:${application.contact_email}`} className="text-sm text-[#00F0FF] hover:underline">
              {application.contact_email}
            </a>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Services built</div>
            <p className="text-neutral-200 text-sm leading-relaxed whitespace-pre-wrap">{application.services_built}</p>
          </div>

          {application.typical_project_size_usd ? (
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Typical project size</div>
              <div className="text-white">${application.typical_project_size_usd.toLocaleString()} USD</div>
            </div>
          ) : null}

          <div className="p-4 bg-white/[0.03] border border-[#00F0FF]/20 rounded-lg">
            <div className="text-xs uppercase tracking-wider text-[#00F0FF] mb-2 font-bold">Proposed terms</div>
            <TermsDelta app={application} />
          </div>

          {application.notes ? (
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Notes from applicant</div>
              <p className="text-neutral-200 text-sm leading-relaxed whitespace-pre-wrap">{application.notes}</p>
            </div>
          ) : null}

          {application.status === 'approved' && application.partner_slug ? (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-sm">
              <div className="text-green-400 font-semibold mb-0.5">Live on the marketplace</div>
              <div className="text-neutral-300">
                Slug: <code className="text-[#00F0FF]">{application.partner_slug}</code>
              </div>
            </div>
          ) : null}

          {application.status === 'rejected' && application.rejection_note ? (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm">
              <div className="text-red-400 font-semibold mb-0.5">Rejection note</div>
              <div className="text-neutral-300">{application.rejection_note}</div>
            </div>
          ) : null}
        </div>

        {application.status === 'pending' ? (
          <div className="p-6 border-t border-white/10 sticky bottom-0 bg-neutral-950 space-y-3">
            {showRejectBox ? (
              <>
                <Textarea
                  placeholder="Optional — short note sent to applicant (e.g. 'thanks, but not a fit right now')"
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  className="bg-black/50 border-white/20 text-white"
                  data-testid="build-partner-reject-note"
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setShowRejectBox(false); setRejectNote(''); }}
                    disabled={busy === 'reject'}
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => act('reject')}
                    disabled={busy === 'reject'}
                    className="flex-1 bg-red-500 text-white hover:bg-red-600"
                    data-testid="build-partner-reject-confirm"
                  >
                    {busy === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm reject'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowRejectBox(true)}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                  data-testid="build-partner-reject-button"
                >
                  Reject
                </Button>
                <Button
                  onClick={() => act('approve')}
                  disabled={busy === 'approve'}
                  className="flex-1 bg-[#00F0FF] text-black font-bold hover:bg-[#00F0FF]/90"
                  data-testid="build-partner-approve-button"
                >
                  {busy === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve & list partner'}
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function AdminBuildPartnerApplications() {
  const [apps, setApps] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(null);
  const [testBusy, setTestBusy] = useState(null);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/partner-announcements?status=scheduled`,
        { headers: authHeaders() },
      );
      setAnnouncements(res.data.announcements || []);
    } catch {
      /* non-fatal — the pending list is a nicety */
    }
  }, []);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/build-partner-applications${status ? `?status=${status}` : ''}`;
      const res = await axios.get(url, { headers: authHeaders() });
      setApps(res.data.applications || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load applications');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { fetchApps(); }, [fetchApps]);
  useEffect(() => {
    fetchAnnouncements();
    const id = setInterval(fetchAnnouncements, 15000); // refresh countdowns
    return () => clearInterval(id);
  }, [fetchAnnouncements]);

  const cancelAnnouncement = async (announcementId) => {
    setCancelBusy(announcementId);
    try {
      await axios.post(
        `${API_URL}/api/admin/partner-announcements/${announcementId}/cancel`,
        {},
        { headers: authHeaders() },
      );
      toast.success('Announcement cancelled — nothing will be sent.');
      fetchAnnouncements();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    } finally {
      setCancelBusy(null);
    }
  };

  const sendTestAnnouncement = async (announcementId) => {
    setTestBusy(announcementId);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/partner-announcements/${announcementId}/send-test`,
        {},
        { headers: authHeaders() },
      );
      toast.success(
        `Test email sent to ${res.data.to} — check your inbox to preview.`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setTestBusy(null);
    }
  };

  return (
    <AdminLayout>
      <div className="px-6 py-8" data-testid="admin-build-partner-applications">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <Building2 className="w-7 h-7 text-[#00F0FF]" />
              Build Partner applications
            </h1>
            <p className="text-neutral-400 text-sm mt-1 max-w-2xl">
              B2B AI dev agencies applying to the referral-rev-share program. Approving auto-lists them under <b>AI Build Partners</b> on the marketplace.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchApps}
            disabled={loading}
            className="border-white/20 text-white hover:bg-white/5"
            data-testid="build-partner-refresh"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              onClick={() => setStatus(f.value)}
              data-testid={`build-partner-filter-${f.value || 'all'}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition ${
                status === f.value
                  ? 'bg-[#00F0FF]/15 border-[#00F0FF]/60 text-[#00F0FF]'
                  : 'bg-white/[0.02] border-white/10 text-neutral-300 hover:border-white/30'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {announcements.length > 0 ? (
          <div
            className="mb-6 p-4 bg-[#00F0FF]/[0.04] border border-[#00F0FF]/40 rounded-xl"
            data-testid="pending-announcements-block"
          >
            <div className="flex items-center gap-2 mb-3">
              <Megaphone className="w-5 h-5 text-[#00F0FF]" />
              <h2 className="text-base font-bold text-white">
                Auto-spotlight scheduled · {announcements.length} pending
              </h2>
            </div>
            <p className="text-xs text-neutral-300 mb-3 leading-relaxed">
              New partners get a newsletter blast 30 minutes after they're
              added. Hit Cancel below if you spot a typo or need to edit
              the partner page before the email lands.
            </p>
            <div className="space-y-2">
              {announcements.map((a) => {
                const sendAt = new Date(a.scheduled_send_at);
                const minsLeft = Math.max(0, Math.round((sendAt - Date.now()) / 60000));
                return (
                  <div
                    key={a.announcement_id}
                    className="flex items-center justify-between gap-3 p-3 bg-black/40 border border-white/10 rounded-lg"
                    data-testid={`announcement-row-${a.announcement_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white font-semibold truncate">
                        {a.partner_name || a.partner_slug}
                      </div>
                      <div className="text-xs text-neutral-400 mt-0.5">
                        Sends in <span className="text-[#00F0FF] font-semibold">
                          {minsLeft === 0 ? 'less than a minute' : `${minsLeft} min`}
                        </span> · source: <span className="text-neutral-300">{a.source}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => sendTestAnnouncement(a.announcement_id)}
                        disabled={testBusy === a.announcement_id}
                        data-testid={`announcement-test-${a.announcement_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-50"
                      >
                        {testBusy === a.announcement_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3" />
                        )}
                        Send test to me
                      </button>
                      <button
                        onClick={() => cancelAnnouncement(a.announcement_id)}
                        disabled={cancelBusy === a.announcement_id}
                        data-testid={`announcement-cancel-${a.announcement_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                      >
                        {cancelBusy === a.announcement_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        Cancel
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="py-20 text-center text-neutral-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : apps.length === 0 ? (
          <div className="py-16 text-center bg-white/[0.02] border border-white/10 rounded-xl">
            <AlertTriangle className="w-8 h-8 text-neutral-500 mx-auto mb-3" />
            <div className="text-white font-semibold">No {status || 'recent'} applications</div>
            <p className="text-neutral-500 text-sm mt-1">
              Share the public form: <code className="text-[#00F0FF]">/build-partners/apply</code>
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {apps.map((app) => (
              <button
                key={app.application_id}
                onClick={() => setSelected(app)}
                data-testid={`build-partner-row-${app.application_id}`}
                className="w-full text-left p-4 bg-white/[0.02] border border-white/10 rounded-xl hover:border-[#00F0FF]/40 transition"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <StatusPill status={app.status} />
                      <span className="text-xs text-neutral-500">{relativeDate(app.created_at)}</span>
                    </div>
                    <div className="text-white font-bold text-lg truncate">{app.company_name}</div>
                    <div className="text-neutral-400 text-sm truncate">
                      {app.contact_name} · {app.contact_email}
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="text-neutral-400">${app.proposed_per_lead_fee_usd}/lead</div>
                    <div className="text-neutral-400">{app.proposed_rev_share_pct}% rev share</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <ApplicationDrawer
          application={selected}
          onClose={() => setSelected(null)}
          onAction={() => { setSelected(null); fetchApps(); }}
        />
      </div>
    </AdminLayout>
  );
}
